import dayjs from 'dayjs';
import { type ApiResponse } from '@/api/core/_models';
import {
  type MockCourse,
  type MockModule,
  type MockTeamMember,
  MOCK_COURSES,
  MOCK_CURRENT_MEMBER_ACTIVITY,
  MOCK_CURRENT_MEMBER_ID,
  MOCK_TEAM,
} from './_mock';
import {
  EnrollmentStatus,
  type LessonBlock,
  LessonBlockKind,
  type LessonCompletionInput,
  LessonKind,
  LessonState,
  type MyCourse,
  type MyCourseDetail,
  type MyCourseModule,
  type MyLesson,
  type TeamBoard,
  type TeamBoardCell,
  type TeamBoardCourse,
  type TeamBoardMember,
} from './_models';

type CourseDates = { startedAt?: string; lastActivityAt?: string; completedAt?: string };

type CourseLesson = Omit<MyLesson, 'state'>;

type Enrollment = { memberId: string; courseId: string; doneCount: number; lessonCount: number };

const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const NEW_COURSE_MAX_DAYS = 7;

const STATUS_RANK: Record<EnrollmentStatus, number> = {
  [EnrollmentStatus.IN_PROGRESS]: 0,
  [EnrollmentStatus.NOT_STARTED]: 1,
  [EnrollmentStatus.DONE]: 2,
};

function resolveMockResponse<T>(data: T): Promise<ApiResponse<T>> {
  const responseData = structuredClone(data);
  const response: ApiResponse<T> = { success: true, status: 200, data: responseData, length: 1 };
  return Promise.resolve(response);
}

function rejectMockResponse(status: number, message: string): Promise<never> {
  const error = new Error(message);
  const responseError = Object.assign(error, { status });
  return Promise.reject(responseError);
}

function stampToIso(daysAgo: number, time: string, base: Date): string {
  const [hour, minute] = time.split(':').map(Number);
  const date = dayjs(base).subtract(daysAgo, 'day').hour(hour).minute(minute).second(0);
  return date.format(DATE_TIME_FORMAT);
}

function listCourseLessons(courseId: string, modules: MockModule[]): CourseLesson[] {
  const lessons: CourseLesson[] = [];
  modules.forEach((module, moduleIndex) => {
    module.lessons.forEach((lesson) => {
      const number = lessons.length + 1;
      const { title, durationMinutes, blocks } = lesson;
      lessons.push({
        id: `${courseId}-aula-${number}`,
        number,
        title,
        moduleNumber: moduleIndex + 1,
        durationMinutes,
        kind: lessonKindOf(blocks),
        blocks,
      });
    });
  });
  return lessons;
}

function lessonKindOf(blocks: LessonBlock[]): LessonKind {
  const kinds = new Set(blocks.map((block) => block.kind));
  if (kinds.has(LessonBlockKind.VIDEO)) return LessonKind.VIDEO;
  if (kinds.has(LessonBlockKind.PDF)) return LessonKind.TEXT_AND_PDF;
  return LessonKind.TEXT;
}

function enrollmentStatusOf(doneCount: number, lessonCount: number): EnrollmentStatus {
  if (doneCount === 0) return EnrollmentStatus.NOT_STARTED;
  if (doneCount === lessonCount) return EnrollmentStatus.DONE;
  return EnrollmentStatus.IN_PROGRESS;
}

function isEnrolled(memberIds: string[] | undefined, memberId: string): boolean {
  if (!memberIds) return true;
  return memberIds.includes(memberId);
}

function compareCoursesForShowcase(first: MyCourse, second: MyCourse): number {
  const rankDifference = STATUS_RANK[first.status] - STATUS_RANK[second.status];
  if (rankDifference !== 0) return rankDifference;
  if (first.status === EnrollmentStatus.IN_PROGRESS) {
    return (second.lastActivityAt ?? '').localeCompare(first.lastActivityAt ?? '');
  }
  return second.publishedAt.localeCompare(first.publishedAt);
}

function findCurrentMemberCourse(courseId: string): MockCourse | undefined {
  const course = MOCK_COURSES.find((item) => item.id === courseId);
  if (!course) return undefined;
  if (!isEnrolled(course.memberIds, MOCK_CURRENT_MEMBER_ID)) return undefined;
  return course;
}

function buildTeamBoardCell(enrollment: Enrollment | undefined): TeamBoardCell | null {
  if (!enrollment) return null;
  const status = enrollmentStatusOf(enrollment.doneCount, enrollment.lessonCount);
  const progressPct = Math.round((enrollment.doneCount / enrollment.lessonCount) * 100);
  return { status, progressPct };
}

function buildTeamBoardMember(member: MockTeamMember, memberEnrollments: Enrollment[]): TeamBoardMember {
  const cells = MOCK_COURSES.map((course) =>
    buildTeamBoardCell(memberEnrollments.find((item) => item.courseId === course.id)),
  );
  const doneEnrollments = memberEnrollments.filter((enrollment) => enrollment.doneCount === enrollment.lessonCount);
  return {
    id: member.id,
    name: member.name,
    initials: member.initials,
    role: member.role,
    isCurrentUser: member.id === MOCK_CURRENT_MEMBER_ID,
    cells,
    doneCount: doneEnrollments.length,
    openCount: memberEnrollments.length,
  };
}

export function createMockTrainingRequests(now: () => Date = () => new Date()) {
  const createdAt = now();
  const completedLessonIds: Record<string, Record<string, string[]>> = {};
  const currentMemberDates: Record<string, CourseDates> = {};

  MOCK_TEAM.forEach((member) => {
    completedLessonIds[member.id] = {};
    MOCK_COURSES.forEach((course, courseIndex) => {
      const doneLessonCount = member.doneLessonCounts[courseIndex];
      if (doneLessonCount === null) return;
      const lessonIds = listCourseLessons(course.id, course.modules).map((lesson) => lesson.id);
      completedLessonIds[member.id][course.id] = lessonIds.slice(0, doneLessonCount);
    });
  });

  Object.entries(MOCK_CURRENT_MEMBER_ACTIVITY).forEach(([courseId, activity]) => {
    const dates: CourseDates = {};
    if (activity.startedAt)
      dates.startedAt = stampToIso(activity.startedAt.daysAgo, activity.startedAt.time, createdAt);
    if (activity.lastActivityAt)
      dates.lastActivityAt = stampToIso(activity.lastActivityAt.daysAgo, activity.lastActivityAt.time, createdAt);
    if (activity.completedAt)
      dates.completedAt = stampToIso(activity.completedAt.daysAgo, activity.completedAt.time, createdAt);
    currentMemberDates[courseId] = dates;
  });

  function buildCourse(course: MockCourse): MyCourse {
    const lessons = listCourseLessons(course.id, course.modules);
    const doneCount = completedLessonIds[MOCK_CURRENT_MEMBER_ID][course.id].length;
    const status = enrollmentStatusOf(doneCount, lessons.length);
    const dates = currentMemberDates[course.id] ?? {};
    const durationMinutes = lessons.reduce((total, lesson) => total + lesson.durationMinutes, 0);
    const isRecentlyPublished = course.publishedAt.daysAgo <= NEW_COURSE_MAX_DAYS;
    const currentLesson = findCurrentLesson(course.id, lessons);
    const nextLesson = currentLesson && {
      id: currentLesson.id,
      number: currentLesson.number,
      title: currentLesson.title,
      moduleNumber: currentLesson.moduleNumber,
      durationMinutes: currentLesson.durationMinutes,
      kind: currentLesson.kind,
    };

    return {
      id: course.id,
      title: course.title,
      description: course.description,
      tag: course.tag,
      coverUrl: `/mock/trainings/covers/${course.id}.jpg`,
      coverAlt: course.coverAlt,
      durationMinutes,
      moduleCount: course.modules.length,
      lessonCount: lessons.length,
      doneCount,
      progressPct: Math.round((doneCount / lessons.length) * 100),
      status,
      isNew: isRecentlyPublished && status === EnrollmentStatus.NOT_STARTED,
      publishedAt: stampToIso(course.publishedAt.daysAgo, course.publishedAt.time, createdAt),
      updatedAt: stampToIso(course.updatedAt.daysAgo, course.updatedAt.time, createdAt),
      ...dates,
      ...(nextLesson && { nextLesson }),
    };
  }

  function findCurrentLesson(courseId: string, lessons: CourseLesson[]): CourseLesson | undefined {
    const doneLessonIds = new Set(completedLessonIds[MOCK_CURRENT_MEMBER_ID][courseId]);
    return lessons.find((lesson) => !doneLessonIds.has(lesson.id));
  }

  function buildCourseDetail(course: MockCourse): MyCourseDetail {
    const lessons = listCourseLessons(course.id, course.modules);
    const doneLessonIds = new Set(completedLessonIds[MOCK_CURRENT_MEMBER_ID][course.id]);
    const currentLesson = findCurrentLesson(course.id, lessons);

    function lessonState(lessonId: string): LessonState {
      if (doneLessonIds.has(lessonId)) return LessonState.DONE;
      if (lessonId === currentLesson?.id) return LessonState.CURRENT;
      return LessonState.NOT_SEEN;
    }

    const modules: MyCourseModule[] = course.modules.map((module, moduleIndex) => {
      const moduleNumber = moduleIndex + 1;
      const moduleLessons: MyLesson[] = lessons
        .filter((lesson) => lesson.moduleNumber === moduleNumber)
        .map((lesson) => ({ ...lesson, state: lessonState(lesson.id) }));
      const moduleDoneCount = moduleLessons.filter((lesson) => lesson.state === LessonState.DONE).length;
      return {
        id: `${course.id}-modulo-${moduleNumber}`,
        number: moduleNumber,
        title: module.title,
        doneCount: moduleDoneCount,
        lessons: moduleLessons,
      };
    });

    return { ...buildCourse(course), learningGoals: course.learningGoals, modules };
  }

  function listEnrollments(): Enrollment[] {
    const enrollments: Enrollment[] = [];
    MOCK_TEAM.forEach((member) => {
      MOCK_COURSES.forEach((course) => {
        if (!isEnrolled(course.memberIds, member.id)) return;
        const doneCount = completedLessonIds[member.id][course.id].length;
        const lessonCount = listCourseLessons(course.id, course.modules).length;
        enrollments.push({ memberId: member.id, courseId: course.id, doneCount, lessonCount });
      });
    });
    return enrollments;
  }

  function listMyCourses(): Promise<ApiResponse<MyCourse[]>> {
    const enrolledCourses = MOCK_COURSES.filter((course) => isEnrolled(course.memberIds, MOCK_CURRENT_MEMBER_ID));
    const courses = enrolledCourses.map((course) => buildCourse(course));
    const sortedCourses = [...courses];
    sortedCourses.sort(compareCoursesForShowcase);
    return resolveMockResponse(sortedCourses);
  }

  function getMyCourse(courseId: string): Promise<ApiResponse<MyCourseDetail>> {
    const course = findCurrentMemberCourse(courseId);
    if (!course) return rejectMockResponse(404, 'Este curso não está disponível para você.');
    return resolveMockResponse(buildCourseDetail(course));
  }

  function setLessonCompletion(courseId: string, input: LessonCompletionInput): Promise<ApiResponse<MyCourseDetail>> {
    const course = findCurrentMemberCourse(courseId);
    if (!course) return rejectMockResponse(404, 'Este curso não está disponível para você.');
    const lessons = listCourseLessons(course.id, course.modules);
    const lesson = lessons.find((item) => item.id === input.lessonId);
    if (!lesson) return rejectMockResponse(404, 'Esta aula não existe neste curso.');

    const otherLessonIds = completedLessonIds[MOCK_CURRENT_MEMBER_ID][course.id].filter((id) => id !== lesson.id);
    const doneLessonIds = input.completed ? [...otherLessonIds, lesson.id] : otherLessonIds;
    completedLessonIds[MOCK_CURRENT_MEMBER_ID][course.id] = doneLessonIds;

    const currentTime = dayjs(now()).format(DATE_TIME_FORMAT);
    const dates: CourseDates = currentMemberDates[course.id] ?? {};
    dates.lastActivityAt = currentTime;
    if (input.completed) dates.startedAt ??= currentTime;
    if (doneLessonIds.length === 0) delete dates.startedAt;
    if (doneLessonIds.length === lessons.length) dates.completedAt ??= currentTime;
    if (doneLessonIds.length < lessons.length) delete dates.completedAt;
    currentMemberDates[course.id] = dates;

    return resolveMockResponse(buildCourseDetail(course));
  }

  function getTeamBoard(): Promise<ApiResponse<TeamBoard>> {
    const enrollments = listEnrollments();

    const courses: TeamBoardCourse[] = MOCK_COURSES.map((course) => {
      const courseEnrollments = enrollments.filter((enrollment) => enrollment.courseId === course.id);
      const doneEnrollments = courseEnrollments.filter((enrollment) => enrollment.doneCount === enrollment.lessonCount);
      return {
        id: course.id,
        shortTitle: course.shortTitle,
        coverUrl: `/mock/trainings/covers/${course.id}.jpg`,
        isNew: course.publishedAt.daysAgo <= NEW_COURSE_MAX_DAYS,
        doneCount: doneEnrollments.length,
        openCount: courseEnrollments.length,
      };
    });

    const members: TeamBoardMember[] = MOCK_TEAM.map((member) => {
      const memberEnrollments = enrollments.filter((enrollment) => enrollment.memberId === member.id);
      return buildTeamBoardMember(member, memberEnrollments);
    });
    members.sort((first, second) => first.name.localeCompare(second.name, 'pt-BR'));

    const notStartedMemberNames = members
      .filter((member) => member.cells.every((cell) => cell === null || cell.status === EnrollmentStatus.NOT_STARTED))
      .map((member) => member.name);

    const progressSum = enrollments.reduce(
      (total, enrollment) => total + enrollment.doneCount / enrollment.lessonCount,
      0,
    );
    const board: TeamBoard = {
      courses,
      members,
      doneCount: courses.reduce((total, course) => total + course.doneCount, 0),
      openCount: enrollments.length,
      progressPct: Math.round((progressSum / enrollments.length) * 100),
      notStartedMemberNames,
    };
    return resolveMockResponse(board);
  }

  return { listMyCourses, getMyCourse, setLessonCompletion, getTeamBoard };
}
